import React, { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const CATEGORIES = [
  "Nagrobek",
  "Schody",
  "Parapety",
  "Blat kuchenny / łazienkowy",
  "Renowacja",
  "Liternictwo / grawerowanie",
  "Element architektoniczny",
  "Inne",
];

const initial = {
  name: "",
  email: "",
  phone: "",
  category: CATEGORIES[0],
  message: "",
};

export default function QuoteDialog({ open, onClose }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Uzupełnij imię, e-mail oraz opis zlecenia.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contacts", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: `Wycena: ${form.category}`,
        message: form.message,
        kind: "quote",
      });
      toast.success("Zapytanie o wycenę zostało wysłane.");
      setForm(initial);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="quote-dialog"
      className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 md:p-8 overflow-y-auto"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="hairline bg-[#0a0a0a] w-full max-w-2xl my-8"
      >
        <div className="flex items-center justify-between p-6 md:p-8 border-b border-white/10">
          <div>
            <p className="overline mb-1">Darmowa wycena</p>
            <h3 className="font-serif-display text-white text-2xl md:text-3xl">
              Opisz swoje zlecenie
            </h3>
          </div>
          <button
            data-testid="quote-dialog-close"
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-white p-2"
            aria-label="Zamknij"
          >
            <X size={22} strokeWidth={1} />
          </button>
        </div>

        <div className="p-6 md:p-8 space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Imię i nazwisko *">
              <input
                data-testid="quote-input-name"
                value={form.name}
                onChange={update("name")}
                type="text"
                required
                className={inputCls}
              />
            </Field>
            <Field label="E-mail *">
              <input
                data-testid="quote-input-email"
                value={form.email}
                onChange={update("email")}
                type="email"
                required
                className={inputCls}
              />
            </Field>
            <Field label="Telefon">
              <input
                data-testid="quote-input-phone"
                value={form.phone}
                onChange={update("phone")}
                type="tel"
                className={inputCls}
              />
            </Field>
            <Field label="Kategoria">
              <select
                data-testid="quote-input-category"
                value={form.category}
                onChange={update("category")}
                className={`${inputCls} appearance-none`}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c} className="bg-[#0a0a0a]">
                    {c}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Opis / szczegóły *">
            <textarea
              data-testid="quote-input-message"
              value={form.message}
              onChange={update("message")}
              rows={5}
              required
              placeholder="Krótko opisz projekt: wymiary, materiał, termin..."
              className={`${inputCls} resize-none`}
            />
          </Field>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              data-testid="quote-submit"
              disabled={loading}
              className="btn-primary disabled:opacity-50"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : null}
              {loading ? "Wysyłanie..." : "Wyślij zapytanie"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

const inputCls =
  "w-full bg-transparent border border-white/15 text-white px-4 py-3 text-sm focus:outline-none focus:border-white transition-colors placeholder:text-neutral-600";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block overline text-neutral-400 mb-2">{label}</span>
    {children}
  </label>
);
